import React from "react";
import { Box } from "@mui/material";

/**
 * Renders an analogue clock face showing the given time.
 * @param {number} hours - 1-12
 * @param {number} minutes - 0-59
 * @param {number} size - width/height in px
 */
const AnalogClock = ({ hours = 12, minutes = 0, size = 220 }) => {
    const r = size / 2 - 8;
    const cx = size / 2;
    const cy = size / 2;

    // Hour hand: 30 deg per hour, plus 0.5 deg per minute
    const hourAngle = ((hours % 12) * 30 + minutes * 0.5 - 90) * (Math.PI / 180);
    const hourLength = r * 0.5;
    const hourX = cx + Math.cos(hourAngle) * hourLength;
    const hourY = cy + Math.sin(hourAngle) * hourLength;

    // Minute hand: 6 deg per minute
    const minuteAngle = (minutes * 6 - 90) * (Math.PI / 180);
    const minuteLength = r * 0.75;
    const minuteX = cx + Math.cos(minuteAngle) * minuteLength;
    const minuteY = cy + Math.sin(minuteAngle) * minuteLength;

    const clockNumbers = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    return (
        <Box
            component="svg"
            viewBox={`0 0 ${size} ${size}`}
            width={size}
            height={size}
            sx={{ display: "block", mx: "auto" }}
            aria-label={`Clock showing ${hours}:${String(minutes).padStart(2, "0")}`}
        >
            {/* Outer circle / bezel */}
            <circle
                cx={cx}
                cy={cy}
                r={r + 4}
                fill="none"
                stroke="#e53935"
                strokeWidth={6}
            />
            {/* Face */}
            <circle
                cx={cx}
                cy={cy}
                r={r}
                fill="#fff"
                stroke="#bdbdbd"
                strokeWidth={2}
            />
            {/* Minute tick marks */}
            {Array.from({ length: 60 }).map((_, i) => {
                const angle = ((i * 6 - 90) * Math.PI) / 180;
                const isMajor = i % 5 === 0;
                const innerR = isMajor ? r - 12 : r - 6;
                const outerR = r;
                const x1 = cx + Math.cos(angle) * innerR;
                const y1 = cy + Math.sin(angle) * innerR;
                const x2 = cx + Math.cos(angle) * outerR;
                const y2 = cy + Math.sin(angle) * outerR;
                return (
                    <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={isMajor ? "#e53935" : "#e0e0e0"}
                        strokeWidth={isMajor ? 2.5 : 1}
                    />
                );
            })}
            {/* Hour numbers */}
            {clockNumbers.map((num, i) => {
                const angle = ((i * 30 - 90) * Math.PI) / 180;
                const nr = r - 28;
                const x = cx + Math.cos(angle) * nr;
                const y = cy + Math.sin(angle) * nr;
                return (
                    <text
                        key={num}
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            fill: "#212121",
                            fontFamily: "system-ui, sans-serif",
                        }}
                    >
                        {num}
                    </text>
                );
            })}
            {/* Hour hand */}
            <line
                x1={cx}
                y1={cy}
                x2={hourX}
                y2={hourY}
                stroke="#1976d2"
                strokeWidth={5}
                strokeLinecap="round"
            />
            {/* Minute hand */}
            <line
                x1={cx}
                y1={cy}
                x2={minuteX}
                y2={minuteY}
                stroke="#e53935"
                strokeWidth={3}
                strokeLinecap="round"
            />
            {/* Center dot */}
            <circle cx={cx} cy={cy} r={6} fill="#b2ebf2" stroke="#00838f" strokeWidth={1} />
        </Box>
    );
};

export default AnalogClock;
